import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { TRADE_PANEL_COLLAPSED_KEY } from 'constants/trades';
import { useIsMobile } from 'hooks/useIsMobile';

const storageKey = (userId: string | number | undefined) =>
  `${TRADE_PANEL_COLLAPSED_KEY}.${userId ?? 'anon'}`;

// Tri-state on purpose. null = the user has never chosen, so the breakpoint
// default applies (collapsed on mobile, expanded on desktop). Once they toggle,
// the stored choice wins on every breakpoint.
const readStored = (userId: string | number | undefined): boolean | null => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw === 'true' ? true : raw === 'false' ? false : null;
  } catch {
    return null;
  }
};

/** Collapsed flag for the /sending and /receiving panel, shared by both pages. */
export const useTradePanelCollapsed = (): [boolean, (collapsed: boolean) => void] => {
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const isMobile = useIsMobile();
  const [stored, setStored] = useState<boolean | null>(() => readStored(userId));

  useEffect(() => {
    setStored(readStored(userId));
  }, [userId]);

  const setCollapsed = useCallback(
    (collapsed: boolean) => {
      setStored(collapsed);
      try {
        localStorage.setItem(storageKey(userId), String(collapsed));
      } catch {
        // localStorage unavailable — keep the in-memory value
      }
    },
    [userId]
  );

  return [stored ?? isMobile, setCollapsed];
};
